import { Button, TextField, Grid, Typography, ToggleButton, ToggleButtonGroup, Box, Divider } from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers"
import { FormEvent, useRef, useState } from "react";
import { AccountData } from "../../types";
import { UploadDialog } from './UploadDialog';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { useFileUpload } from './useFileUpload';
import UploadIcon from '@mui/icons-material/Upload';

export function AccountVerificationTask({ onSubmit }: {
  onSubmit: (account: any) => void
}) {
  const [account, setAccount] = useState<AccountData>({
    firstName: '',
    middleName: '',
    lastName: '',
    businessName: '',
    accountNumber: '',
    routingNumber: '',
    address: '',
    city: '',
    state: '',
    zip: '',
    ssn: '',
    dateOfBirth: '',
    workPhone: '',
    homePhone: '',
    bankName: '',
    specialInstructions: '',
  })
  const [accountNumberConfirm, setAccountNumberConfirm] = useState('');
  const [accountNumberConfirmError, setAccountNumberConfirmError] = useState(false)
  const [homePhoneError, setHomePhoneError] = useState(false)
  const [workPhoneError, setWorkPhoneError] = useState(false)
  const accountNumberConfirmRef = useRef<HTMLInputElement>(null);
  const homePhoneRef = useRef<HTMLInputElement>(null);
  const workPhoneRef = useRef<HTMLInputElement>(null);

  const unSubmittableErrors: Array<{ error: boolean, ref: React.RefObject<HTMLInputElement> }> = [{ error: accountNumberConfirmError, ref: accountNumberConfirmRef }, { error: homePhoneError, ref: homePhoneRef }, { error: workPhoneError, ref: workPhoneRef }];

  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  // DEV Helper:
  // useEffect(() => setAccount({ firstName: 'Tim', lastName: 'Doe', businessName: '', accountNumber: '91201050067', routingNumber: '122199983', address: '4026 STRATMORE AVE', city: 'YOUNGSTOWN', state: 'OH', zip: '445113548', }), [])

  const [individualOrBusiness, setIndividualOrBusiness] = useState<'individual' | 'business'>('individual')

  const { uploadInput, fileUploadBody, fileUploadActions, isComplete } = useFileUpload({})

  function handleSubmit(event: FormEvent) {
    event.preventDefault()

    if (unSubmittableErrors.some(error => Boolean(error.error))) {
      const errorWithRef = unSubmittableErrors.find(error => error.error && error.ref.current);
      if (errorWithRef && errorWithRef.ref.current) {
        errorWithRef.ref.current.focus();
      }
      return
    }

    //Remove non numeric characters in phone numbers for USBank API.
    if (account?.homePhone) {
      account.homePhone = account.homePhone.replace(/\D/g, '')
    }
    if (account?.workPhone) {
      account.workPhone = account.workPhone.replace(/\D/g, '')
    }

    let accountPayload: AccountData

    // The 3rd party validation api doesn't like '' or null values.
    if (individualOrBusiness === 'individual') {
      accountPayload = { ...account, businessName: undefined }
    } else {
      accountPayload = { ...account, firstName: undefined, lastName: undefined }
    }

    onSubmit(accountPayload)
  }

  // Generic helper function to handle input fields that need to match
  const handleMatchValidator = (event: any, setStateError: React.Dispatch<React.SetStateAction<boolean>>, comparedValue: string): void => {
    const inputValue = event.target.value
    if (inputValue !== comparedValue) {
      setStateError(true)
    } else {
      setStateError(false)
    }
  }
  const handlePhoneValidator = (event: any, setStateError: React.Dispatch<React.SetStateAction<boolean>>): void => {
    const inputValue = event.target.value
    if (/^(\+\d{1,2}\s)?\(?\d{3}\)?[\s.-]?\d{3}[\s.-]?\d{4}$/.test(inputValue)) {
      setStateError(false)
    } else {
      setStateError(true)
    }
  }

  const handleUploadClick = (e: any) => {
    setUploadDialogOpen(true);
  };

  const handleUploadDialogClose = () => {
    setUploadDialogOpen(false);
  };

  const uploadButton = (
    <Button onClick={handleUploadClick} component="label" startIcon={<UploadIcon />} variant="outlined">
      Upload
      {uploadInput}
    </Button>
  );

  const textFieldProps = {
    variant: "outlined",
    fullWidth: true,
    sx: {
      backgroundColor: 'white',
    },
    margin: 'normal',
  } as const

  return (
    <form onSubmit={handleSubmit}>
      <Box sx={{ p: 2, mb: 1 }}>
        <Typography variant="h6">Account Ownership Details</Typography>

        <Grid container justifyContent="center" mt={2}>
          <ToggleButtonGroup
            value={individualOrBusiness}
            exclusive
            onChange={(_, value) => setIndividualOrBusiness(value)}
            fullWidth
          >
            <ToggleButton value="individual" color="primary">
            Individual
            </ToggleButton>
            <ToggleButton value="business" color="primary">
            Business/LLC/Trust
            </ToggleButton>
          </ToggleButtonGroup>
        </Grid>

        {/* Conditionally render the input fields based on selected option */}
        {individualOrBusiness === 'business' ? (
          <TextField
            label="Business/LLC/Trust Name"
            value={account.businessName}
            onChange={(e) => setAccount({ ...account, businessName: e.target.value })}
            inputProps={{ maxLength: 40 }}
            required={true}
            {...textFieldProps}
          />
        ) : (
          <Grid container columnSpacing={{xs: 0, lg: 2}}>
            <Grid item xs={12} lg={4}>
              <TextField
                label="First Name"
                value={account.firstName}
                onChange={(e) => setAccount({ ...account, firstName: e.target.value })}
                inputProps={{ maxLength: 40 }}
                required={true}
                {...textFieldProps}
              />
            </Grid>
            <Grid item xs={12} lg={4}>
              <TextField
                label="Middle Name"
                value={account.middleName}
                onChange={(e) => setAccount({ ...account, middleName: e.target.value })}
                inputProps={{ maxLength: 40 }}
                {...textFieldProps}
              />
            </Grid>
            <Grid item xs={12} lg={4}>
              <TextField
                label="Last Name"
                value={account.lastName}
                onChange={(e) => setAccount({ ...account, lastName: e.target.value })}
                inputProps={{ maxLength: 40 }}
                required={true}
                {...textFieldProps}
              />
            </Grid>
          </Grid>
        )}
        <Grid container columnSpacing={{xs: 0, lg: 2}}>
          <Grid item xs={12} lg={6} xl={3}>
            <TextField
              label="Street Address"
              value={account.address}
              onChange={(e) => setAccount({ ...account, address: e.target.value })}
              inputProps={{ maxLength: 40 }}
              required={true}
              {...textFieldProps}
            />
          </Grid>
          <Grid item xs={12} lg={6} xl={3}>
            <TextField
              label="City"
              value={account.city}
              onChange={(e) => setAccount({ ...account, city: e.target.value })}
              inputProps={{ maxLength: 25 }}
              required={true}
              {...textFieldProps}
            />
          </Grid>
          <Grid item xs={12} lg={6} xl={3}>
            <TextField
              label="State"
              value={account.state}
              onChange={(e) => setAccount({ ...account, state: e.target.value })}
              inputProps={{ minLength: 2, maxLength: 2 }}
              required={true}
              {...textFieldProps}
            />
          </Grid>
          <Grid item xs={12} lg={6} xl={3}>
            <TextField
              label="Zipcode"
              value={account.zip}
              onChange={(e) => setAccount({ ...account, zip: e.target.value })}
              inputProps={{ minLength: 5, maxLength: 10 }}
              required={true}
              {...textFieldProps}
            />
          </Grid>
        </Grid>

        { individualOrBusiness === 'individual' && <Grid container columnSpacing={{xs: 0, lg: 2}}>
          <Grid item xs={12} lg={6}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                label="Date of Birth"
                value={account.dateOfBirth ? new AdapterDayjs().date(account.dateOfBirth) : null}
                onChange={(date) => setAccount({ ...account, dateOfBirth: date ? date.format('YYYYMMDD') : '' })}
                renderInput={(params) => <TextField {...params} {...textFieldProps} />}
              />
            </LocalizationProvider>
          </Grid>
          <Grid item xs={12} lg={6}>
            <TextField
              label="Last 4 of SSN"
              value={account.ssn}
              onChange={(e) => setAccount({ ...account, ssn: e.target.value })}
              inputProps={{ maxLength: 4, minLength: 4 }}
              {...textFieldProps}
            />
          </Grid>
        </Grid> }

        <Grid container columnSpacing={{xs: 0, lg: 2}}>
          <Grid item xs={12} lg={individualOrBusiness === 'individual' ? 6 : 12}>
            <TextField
              label={individualOrBusiness === 'individual' ? "Work Phone" : "Phone"}
              value={account.workPhone}
              onChange={(e) => {
                setAccount({ ...account, workPhone: e.target.value });
                handlePhoneValidator(e, setWorkPhoneError);
              }}
              inputProps={{ maxLength: 12 }}
              {...textFieldProps}
              error={workPhoneError}
              helperText={workPhoneError ? 'Please enter a valid phone number XXX-YYY-ZZZZ' : ''}
            />
          </Grid>
          { individualOrBusiness === 'individual' && <Grid item xs={12} lg={6} >
            <TextField
              label="Home Phone"
              value={account.homePhone}
              onChange={(e) => {
                setAccount({ ...account, homePhone: e.target.value });
                handlePhoneValidator(e, setHomePhoneError);
              }}
              inputProps={{ maxLength: 12 }}
              {...textFieldProps}
              error={homePhoneError}
              helperText={homePhoneError ? 'Please enter a valid phone number XXX-YYY-ZZZZ' : ''}
            />
          </Grid> }
        </Grid>
      </Box>

      <Divider />

      <Box sx={{ p: 2, mb: 1 }}>
        <Typography variant="h6">Banking Information</Typography>
        <TextField
          label="Bank Name"
          value={account.bankName}
          onChange={(e) => setAccount({ ...account, bankName: e.target.value })}
          inputProps={{ maxLength: 40 }}
          {...textFieldProps}
        />
        <TextField
          label="ABA Routing Number"
          value={account.routingNumber}
          onChange={(e) => setAccount({ ...account, routingNumber: e.target.value })}
          inputProps={{ minLength: 9, maxLength: 9 }}
          required={true}
          {...textFieldProps}
        />
        <Grid container columnSpacing={{xs: 0, lg: 2}}>
          <Grid item xs={12} lg={6}>
            <TextField
              label="Account Number"
              value={account.accountNumber}
              onChange={(e) => {
                setAccount({ ...account, accountNumber: e.target.value });
                handleMatchValidator(e, setAccountNumberConfirmError, accountNumberConfirm);
              }}
              inputProps={{ maxLength: 17 }}
              required={true}
              {...textFieldProps}
            />
          </Grid>
          <Grid item xs={12} lg={6}>
            <TextField
              label="Confirm Account Number"
              value={accountNumberConfirm}
              onChange={(e) => {
                setAccountNumberConfirm(e.target.value)
                handleMatchValidator(e, setAccountNumberConfirmError, account.accountNumber)
              }}
              error={accountNumberConfirmError}
              helperText={accountNumberConfirmError ? 'Account Numbers do not match' : ''}
              inputProps={{ maxLength: 17 }}
              inputRef={accountNumberConfirmRef}
              required={true}
              {...textFieldProps}
            />
          </Grid>
        </Grid>
      </Box>

      <Divider />

      <Box sx={{ p: 2, mb: 1 }}>
        <Typography variant="h6">Additional Information</Typography>
        <TextField
          label="Special Instructions/Intermediary Information"
          value={account.specialInstructions}
          onChange={(e) => setAccount({ ...account, specialInstructions: e.target.value })}
          multiline
          rows={4}
          inputProps={{ maxLength: 1024 }}
          {...textFieldProps}
        />

        <Grid container alignItems="center" gap={2} mt={2} bgcolor={"white"} borderRadius={"4px"} padding={"10px"} border={"1px solid rgba(0,0,0,0.175)"} wrap="nowrap">
          <Grid item>
            {uploadButton}
          </Grid>
          <Grid item>
            <Typography paddingLeft={1}>
              If your bank has provided a document with additional instructions please upload it here.
            </Typography>
          </Grid>
        </Grid>
      </Box>

      <Grid container justifyContent="flex-end" sx={{ pr: 2 }}>
        <Button
          type="submit"
          sx={{
            mt: 2,
            pr: 2
          }}
          variant="contained"
        >
          Submit
        </Button>
      </Grid>

      <UploadDialog
        open={uploadDialogOpen}
        onClose={handleUploadDialogClose}
        fileUploadBody={fileUploadBody}
        fileUploadActions={fileUploadActions}
        isComplete={isComplete}
      />
    </form>
  )
}
